/**
 * ComparisonSliderRenderer - Shared shell comparison block
 *
 * Above the small-mobile breakpoint (>576px) this renders the AA-146
 * clip-window / draggable-divider model: both panels are mounted at fixed
 * width and never reflow; two clip wrappers mask the visible portions; a
 * draggable dark divider sits on top. Narrow windows without touch input
 * keep the drag interaction — width alone doesn't disable it.
 *
 * At or below the small-mobile breakpoint (≤576px) this renders the NA-233
 * stacked layout: one panel at a time with a full-width dark toggle bar at
 * the bottom of the card advertising the opposing panel's title. This
 * replaces the AA-144 side-rail toggle entirely.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type {
  ComparisonSliderBlock,
  ComparisonSliderImageSize,
  ComparisonSliderOption,
} from '@course-authoring/shared-types';
import { useTheme, getReadableTextColor, type ThemeTokens } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { useContainerWidth } from './hooks/useContainerWidth';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { useLongestLineWidth } from './hooks/useLongestLineWidth';
import { useVisitedItems } from './hooks/useVisitedItems';
import { createContentBlockStyles, interactionCard } from './styles/contentBlocks';
import { getContentBlockHeadingTypography } from './styles/titleTypography';
import {
  getTextImageSplitGridStyle,
  textImageSplitMobileImageStyle,
} from './styles/textImageSplit';
import {
  createRichTextContentResetStyles,
  createRichTextRendererExtensions,
  extractPlainTextFromRichContent,
  safeGenerateRichTextHTML,
} from './richTextRenderer';
import { ComparisonSliderKnobIcon, ImagePlaceholderIcon } from './contentBlockIcons';
import { InteractionMessage } from './InteractionMessage';
import { getStandardInteractionWidth } from './interactionLayout';
import { useCardBackground } from './SectionSurfaceContext';
import {
  VideoRenderer,
  type MediaPlayerInstance,
  type VideoTranscriptOperations,
} from './VideoRenderer';
import { RichTextContent } from './RichTextContent';

const KNOB_SIZE_PX = 35;
const DRAG_THRESHOLD_PX = 6;
const SNAP_DURATION_MS = 900;
const OVERLAY_FADE_MS = 900;
// Click-toggle uses its own pacing so the auto-driven slide reads quicker
// than a user's long manual drag.
const CLICK_SLIDE_MS = 500;

// Quartic ease-out: very fast start, pronounced settle at the end. The
// incoming panel reads in quickly, then the last bit of overlay clears slowly
// for a "settling" feel.
function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 4);
}
const SNAP_TIMING = 'cubic-bezier(0.4, 0, 0.2, 1)';

// Divider width tracks the AA-144 sidebar clamp(180px, 20%, 240px). We compute
// it imperatively because drag math needs pixel values.
const DIVIDER_MIN_PX = 180;
const DIVIDER_MAX_PX = 240;
const DIVIDER_FRACTION = 0.2;

// Panels compact themselves (text/image split collapses to image-above-text;
// video title/description move below the poster) once their measured content
// width drops below this. Content-width rather than viewport so surrounding
// chrome (course sidebar, editor panels) is accounted for.
const PANEL_COMPACT_MAX_WIDTH_PX = 590;

// NA-233: the stacked layout swaps in above the shared 576px small-mobile
// breakpoint — the drag-slider needs more room than other blocks' desktop
// layouts (mirrors Categorization's block-specific layout breakpoint).
// Typography still steps on the shared 576px tier.
const STACKED_LAYOUT_BREAKPOINT_PX = 650;

function computeDividerWidth(containerWidth: number): number {
  const fluid = containerWidth * DIVIDER_FRACTION;
  return Math.max(DIVIDER_MIN_PX, Math.min(DIVIDER_MAX_PX, fluid));
}

export interface ComparisonSliderRendererProps {
  block: ComparisonSliderBlock;
  className?: string;
  style?: React.CSSProperties;
  onInteractionComplete?: () => void;
  onSideSelected?: (side: 1 | 2) => void;
  onSideVideoPlaybackStarted?: (side: 1 | 2) => void;
  onSideVideoPlaybackCompleted?: (side: 1 | 2) => void;
  lessonThemeColor?: string;
  /** Locale of the composed block payload, independent of the selected catalog locale. */
  currentLocale?: string;
  transcriptOperations?: VideoTranscriptOperations;
}

const contentInnerStyles = createRichTextContentResetStyles('csr-content');

type Side = 'a' | 'b';

interface PanelContentProps {
  option: ComparisonSliderOption;
  titleHtml: string;
  bodyHtml: string;
  contentType: ComparisonSliderBlock['contentType'];
  imageSize: ComparisonSliderImageSize | undefined;
  stackImage: boolean;
  tokens: ThemeTokens;
  styles: ReturnType<typeof createContentBlockStyles>;
  videoRef?: React.Ref<MediaPlayerInstance>;
  lessonThemeColor?: string;
  currentLocale?: string;
  transcriptOperations?: VideoTranscriptOperations;
  onVideoPlaybackStarted?: () => void;
  onVideoPlaybackCompleted?: () => void;
}

function PanelContent({
  option,
  titleHtml,
  bodyHtml,
  contentType,
  imageSize,
  stackImage,
  tokens,
  styles,
  videoRef,
  lessonThemeColor,
  currentLocale,
  transcriptOperations,
  onVideoPlaybackStarted,
  onVideoPlaybackCompleted,
}: PanelContentProps): React.ReactElement {
  if (contentType === 'video') {
    return (
      <div style={{ width: '100%' }}>
        {option.videoUrl ? (
          <VideoRenderer
            ref={videoRef}
            url={option.videoUrl}
            posterUrl={option.videoPosterUrl}
            title={option.videoTitle}
            shortDescription={option.videoShortDescription}
            constrainWidth={false}
            compactText={stackImage}
            lessonThemeColor={lessonThemeColor}
            currentLocale={currentLocale}
            centerstageVideoId={option.centerstageVideoId}
            transcriptOperations={transcriptOperations}
            onPlaybackStarted={onVideoPlaybackStarted}
            onPlaybackCompleted={onVideoPlaybackCompleted}
          />
        ) : (
          <div
            style={{
              width: '100%',
              aspectRatio: '16 / 9',
              backgroundColor: tokens.backgrounds.surfaceAlt,
              borderRadius: tokens.radius.lg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: tokens.text.muted,
              fontFamily: tokens.typography.fontFamily,
              fontSize: tokens.typography.body.size,
            }}
          >
            No video set
          </div>
        )}
      </div>
    );
  }

  const showImage = contentType === 'text-image';

  const panelStyle: React.CSSProperties = stackImage
    ? {
        ...styles.comparisonContentPanel,
        flexDirection: 'column',
        alignItems: 'stretch',
      }
    : !showImage
      ? styles.comparisonContentPanel
      : {
          ...styles.comparisonContentPanel,
          ...getTextImageSplitGridStyle(imageSize),
        };

  const imageStyle: React.CSSProperties = stackImage
    ? {
        ...styles.comparisonOptionImage,
        ...textImageSplitMobileImageStyle,
        maxWidth: 'none',
      }
    : {
        ...styles.comparisonOptionImage,
        width: '100%',
        maxWidth: 'none',
        alignSelf: 'center',
      };

  const image = showImage ? (
    option.imageUrl ? (
      <img
        src={option.imageUrl}
        alt={option.imageAlt || ''}
        style={imageStyle}
      />
    ) : (
      <div
        style={{
          ...imageStyle,
          aspectRatio: '1 / 1',
          backgroundColor: tokens.backgrounds.surfaceAlt,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: tokens.spacing.sm,
          color: tokens.text.muted,
          fontFamily: tokens.typography.fontFamily,
          fontSize: '14px',
        }}
      >
        <ImagePlaceholderIcon />
        Image
      </div>
    )
  ) : null;

  // Stacked: title, then image, then body. Side-by-side: text column
  // (title + body) beside the image.
  if (stackImage) {
    return (
      <div style={panelStyle}>
        <RichTextContent
          className="csr-content"
          style={{ ...styles.comparisonOptionTitle, minWidth: 0 }}
          html={titleHtml}
        />
        {image}
        <RichTextContent
          className="csr-content"
          style={{ ...styles.comparisonOptionBody, minWidth: 0 }}
          html={bodyHtml}
        />
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      <div style={{ minWidth: 0 }}>
        <RichTextContent
          className="csr-content"
          style={styles.comparisonOptionTitle}
          html={titleHtml}
        />
        <RichTextContent
          className="csr-content"
          style={styles.comparisonOptionBody}
          html={bodyHtml}
        />
      </div>
      {image}
    </div>
  );
}

interface SharedRenderArgs {
  block: ComparisonSliderBlock;
  tokens: ThemeTokens;
  styles: ReturnType<typeof createContentBlockStyles>;
  stackImage: boolean;
  option1TitleHtml: string;
  option1BodyHtml: string;
  option2TitleHtml: string;
  option2BodyHtml: string;
  labelA: string;
  labelB: string;
  markSeen: (side: Side) => void;
  lessonThemeColor?: string;
  currentLocale?: string;
  transcriptOperations?: VideoTranscriptOperations;
  onSideSelected?: (side: 1 | 2) => void;
  onSideVideoPlaybackStarted?: (side: 1 | 2) => void;
  onSideVideoPlaybackCompleted?: (side: 1 | 2) => void;
}

export function ComparisonSliderRenderer({
  block,
  className,
  style,
  onInteractionComplete,
  onSideSelected,
  onSideVideoPlaybackStarted,
  onSideVideoPlaybackCompleted,
  lessonThemeColor,
  currentLocale,
  transcriptOperations,
}: ComparisonSliderRendererProps): React.ReactElement {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const extensions = useMemo(() => createRichTextRendererExtensions(), []);
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  // NA-233: the stacked layout kicks in at phone-ish widths; above it the
  // drag-slider persists (narrow window ≠ no pointer/drag).
  const isStacked = useIsMobileViewport(STACKED_LAYOUT_BREAKPOINT_PX);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);

  // Measure the shell so panels compact based on the width they actually get
  // (the desktop slider gives each panel the shell minus divider and padding).
  const shellRef = useRef<HTMLDivElement | null>(null);
  const shellWidth = useContainerWidth(shellRef);
  const panelContentWidth = useMemo(() => {
    if (!shellWidth) return undefined;
    if (isStacked) {
      return shellWidth - 2 * parseFloat(tokens.spacing.lg);
    }
    const panelPadding = parseFloat(tokens.spacing['3xl']) + parseFloat(tokens.spacing['4xl']);
    return shellWidth - computeDividerWidth(shellWidth) - panelPadding;
  }, [shellWidth, isStacked, tokens]);
  const stackImage = isStacked
    || (panelContentWidth !== undefined && panelContentWidth <= PANEL_COMPACT_MAX_WIDTH_PX);

  const styles = useMemo(() => {
    const base = createContentBlockStyles(tokens);
    if (!isSmallMobile) return base;
    // NA-233: reading copy (subtitle, panel title, panel body) steps from
    // 20px to 16px at ≤576px — matches WCS/Likert/Single Choice.
    const mobileBody = tokens.typography.mobile.body;
    return {
      ...base,
      comparisonSubtitle: {
        ...base.comparisonSubtitle,
        fontSize: mobileBody.size,
        lineHeight: mobileBody.lineHeight,
      },
      comparisonOptionTitle: {
        ...base.comparisonOptionTitle,
        fontSize: mobileBody.size,
        lineHeight: mobileBody.lineHeight,
      },
      comparisonOptionBody: {
        ...base.comparisonOptionBody,
        fontSize: mobileBody.size,
        lineHeight: mobileBody.lineHeight,
      },
    };
  }, [tokens, isSmallMobile]);
  const cardBackground = useCardBackground();
  const {
    isComplete: interactionComplete,
    markVisited: markSeen,
  } = useVisitedItems<Side>({
    expectedCount: 2,
    onAllVisited: onInteractionComplete,
    initialVisitedIds: ['a'],
  });

  const requireInteraction = block.requireInteraction ?? false;

  const titleHtml = useMemo(
    () => safeGenerateRichTextHTML(block.title, extensions),
    [block.title, extensions]
  );
  const subtitleHtml = useMemo(
    () => safeGenerateRichTextHTML(block.subtitle, extensions),
    [block.subtitle, extensions]
  );
  const option1TitleHtml = useMemo(
    () => safeGenerateRichTextHTML(block.option1.title, extensions),
    [block.option1.title, extensions]
  );
  const option1BodyHtml = useMemo(
    () => safeGenerateRichTextHTML(block.option1.body, extensions),
    [block.option1.body, extensions]
  );
  const option2TitleHtml = useMemo(
    () => safeGenerateRichTextHTML(block.option2.title, extensions),
    [block.option2.title, extensions]
  );
  const option2BodyHtml = useMemo(
    () => safeGenerateRichTextHTML(block.option2.body, extensions),
    [block.option2.body, extensions]
  );

  const labelA = block.option1.collapsedLabel || t('comparison.show_option', { option: 2 });
  const labelB = block.option2.collapsedLabel || t('comparison.show_option', { option: 1 });

  const comparisonContentWidth = getStandardInteractionWidth(tokens, isMobile);

  const headerStyle: React.CSSProperties = {
    ...styles.comparisonHeader,
    width: comparisonContentWidth,
    maxWidth: '100%',
    margin: `0 auto ${styles.comparisonHeader.marginBottom ?? 0}`,
    boxSizing: 'border-box',
  };

  // NA-227: honor the block's chosen heading level; the renderer previously
  // fixed the title at h2/40px regardless of titleLevel.
  // NA-233: moved to the three-tier ladder so the title steps to the shared
  // narrow-phone sizes (h2 → 26px at ≤576px) like the other content blocks.
  const titleTypography = getContentBlockHeadingTypography(tokens, block.titleLevel ?? 'h2', {
    isMobile,
    isSmallMobile,
  });
  const comparisonTitleStyle: React.CSSProperties = {
    ...styles.comparisonTitle,
    fontSize: titleTypography.size,
    fontWeight: titleTypography.weight,
    lineHeight: titleTypography.lineHeight,
  };

  const shellStyle: React.CSSProperties = {
    ...interactionCard(tokens),
    width: comparisonContentWidth,
    maxWidth: '100%',
    margin: '0 auto',
    backgroundColor: cardBackground,
    padding: 0,
    boxSizing: 'border-box',
    overflow: 'hidden',
  };

  const sharedArgs: SharedRenderArgs = {
    block,
    tokens,
    styles,
    stackImage,
    option1TitleHtml,
    option1BodyHtml,
    option2TitleHtml,
    option2BodyHtml,
    labelA,
    labelB,
    markSeen,
    lessonThemeColor,
    currentLocale,
    transcriptOperations,
    onSideSelected,
    onSideVideoPlaybackStarted,
    onSideVideoPlaybackCompleted,
  };

  return (
    <div
      className={className}
      style={{ ...styles.comparisonContainer, ...style }}
    >
      <style>{contentInnerStyles}</style>

      <div data-testid="comparison-header" style={headerStyle}>
        <RichTextContent
          className="csr-content"
          style={comparisonTitleStyle}
          html={titleHtml}
        />
        <RichTextContent
          className="csr-content"
          style={styles.comparisonSubtitle}
          html={subtitleHtml}
        />
      </div>

      <div ref={shellRef} data-testid="comparison-shell" style={shellStyle}>
        {isStacked ? (
          <StackedComparisonBody {...sharedArgs} />
        ) : (
          <DesktopComparisonBody {...sharedArgs} />
        )}
      </div>

      <InteractionMessage
        variant="boxed"
        requireInteraction={requireInteraction}
        isComplete={interactionComplete}
        message={block.interactionMessage}
        fallbackMessage={t('comparison.explore_both')}
      />
    </div>
  );
}

ComparisonSliderRenderer.displayName = 'ComparisonSliderRenderer';

// ---------- Stacked (NA-233 phone layout: panel above bottom toggle bar) ----------

function StackedComparisonBody({
  block,
  tokens,
  styles,
  stackImage,
  option1TitleHtml,
  option1BodyHtml,
  option2TitleHtml,
  option2BodyHtml,
  labelA,
  labelB,
  markSeen,
  lessonThemeColor,
  currentLocale,
  transcriptOperations,
  onSideSelected,
  onSideVideoPlaybackStarted,
  onSideVideoPlaybackCompleted,
}: SharedRenderArgs): React.ReactElement {
  const [expanded, setExpanded] = useState<Side>('a');
  const videoARef = useRef<MediaPlayerInstance | null>(null);
  const videoBRef = useRef<MediaPlayerInstance | null>(null);

  // The bar advertises the panel the learner is NOT currently viewing, as
  // plain text so authored rich-text colors can't fight the white-on-dark bar
  // styling; an untitled opposing option falls back to the collapsed label.
  const opposingTitleText = extractPlainTextFromRichContent(
    expanded === 'a' ? block.option2.title : block.option1.title
  );
  const barLabel = opposingTitleText || (expanded === 'a' ? labelA : labelB);

  const handleClickToggle = useCallback(() => {
    const nextSide: Side = expanded === 'a' ? 'b' : 'a';
    const leavingVideo = expanded === 'a' ? videoARef : videoBRef;
    leavingVideo.current?.pause();
    setExpanded(nextSide);
    markSeen(nextSide);
    onSideSelected?.(nextSide === 'a' ? 1 : 2);
  }, [expanded, markSeen, onSideSelected]);

  const layoutStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing.xl,
  };

  // Both panels are mounted in the same grid cell so the card holds the
  // height of the taller side — the toggle bar keeps one vertical position
  // regardless of which panel is showing (mirrors the desktop equal-height
  // stage). The hidden panel is removed from the a11y tree via visibility.
  const contentAreaStyle: React.CSSProperties = {
    display: 'grid',
    paddingTop: tokens.spacing.lgPlus,
    paddingLeft: tokens.spacing.lg,
    paddingRight: tokens.spacing.lg,
    paddingBottom: 0,
    boxSizing: 'border-box',
  };

  const panelCellStyle = (side: Side): React.CSSProperties => ({
    gridRowStart: 1,
    gridColumnStart: 1,
    minWidth: 0,
    visibility: expanded === side ? 'visible' : 'hidden',
  });

  // Viewing A: label first, chevron on the right pointing right (toward B).
  // Viewing B: chevron on the left pointing left (back toward A), label after.
  const barStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: expanded === 'a' ? 'row' : 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.md,
    width: '100%',
    minHeight: '70px',
    border: 0,
    padding: `${tokens.spacing.sm} ${tokens.spacing.lgPlus}`,
    backgroundColor: tokens.buttons.action.bg,
    cursor: 'pointer',
    boxSizing: 'border-box',
  };

  const barLabelStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    fontFamily: tokens.typography.fontFamily,
    fontSize: tokens.typography.sizes.base,
    fontWeight: tokens.typography.weights.bold,
    lineHeight: 'normal',
    color: tokens.text.onInverse,
    textAlign: 'left',
  };

  const knobIconStyle: React.CSSProperties = {
    display: 'block',
    flexShrink: 0,
    transform: expanded === 'a' ? 'scaleX(-1)' : undefined,
  };

  return (
    <div style={layoutStyle}>
      <div data-testid="comparison-content" style={contentAreaStyle}>
        <div data-testid="comparison-stacked-panel-a" style={panelCellStyle('a')}>
          <PanelContent
            option={block.option1}
            titleHtml={option1TitleHtml}
            bodyHtml={option1BodyHtml}
            contentType={block.contentType}
            imageSize={block.imageSize}
            stackImage={stackImage}
            tokens={tokens}
            styles={styles}
            videoRef={videoARef}
            lessonThemeColor={lessonThemeColor}
            currentLocale={currentLocale}
            transcriptOperations={transcriptOperations}
            onVideoPlaybackStarted={() => onSideVideoPlaybackStarted?.(1)}
            onVideoPlaybackCompleted={() => onSideVideoPlaybackCompleted?.(1)}
          />
        </div>
        <div data-testid="comparison-stacked-panel-b" style={panelCellStyle('b')}>
          <PanelContent
            option={block.option2}
            titleHtml={option2TitleHtml}
            bodyHtml={option2BodyHtml}
            contentType={block.contentType}
            imageSize={block.imageSize}
            stackImage={stackImage}
            tokens={tokens}
            styles={styles}
            videoRef={videoBRef}
            lessonThemeColor={lessonThemeColor}
            currentLocale={currentLocale}
            transcriptOperations={transcriptOperations}
            onVideoPlaybackStarted={() => onSideVideoPlaybackStarted?.(2)}
            onVideoPlaybackCompleted={() => onSideVideoPlaybackCompleted?.(2)}
          />
        </div>
      </div>

      <button
        type="button"
        data-testid="comparison-toggle"
        style={barStyle}
        onClick={handleClickToggle}
        aria-label={barLabel}
      >
        <span data-testid="comparison-toggle-label" style={barLabelStyle}>
          {barLabel}
        </span>
        <ComparisonSliderKnobIcon
          style={knobIconStyle}
          fill={lessonThemeColor ?? tokens.ui.fixedAccent}
          stroke={getReadableTextColor(lessonThemeColor ?? tokens.ui.fixedAccent)}
        />
      </button>
    </div>
  );
}

// ---------- Desktop (AA-146 clip-window / draggable divider) ----------

// Sets the inner span's explicit width to its longest rendered line (via
// useLongestLineWidth), so auto margins can center the text-block
// horizontally inside the flex wrapper without center-aligning the lines
// themselves.
interface DividerLabelTextProps {
  text: string;
  visible: boolean;
  testId: string;
  wrapperStyle: React.CSSProperties;
  textStyle: React.CSSProperties;
  maxFontSizePx: number;
  minFontSizePx: number;
  // Reports this label's own fitted size; the parent applies the smaller of
  // the two to both so the labels match when the divider swaps them.
  onFit: (px: number) => void;
  appliedFontSizePx: number;
}

// Picks the largest font size in [minPx, maxPx] at which the label's longest
// word fits the wrapper's text area. Words can only wrap at spaces, so a
// single over-wide word would otherwise run under the knob. Returns maxPx
// until measured (and whenever everything already fits).
function useFittedFontSize(
  wrapperRef: React.RefObject<HTMLDivElement | null>,
  text: string,
  maxPx: number,
  minPx: number,
): number {
  const [size, setSize] = useState(maxPx);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const fit = (): void => {
      const available = wrapper.clientWidth;
      if (available <= 0) return;
      const words = text.split(/\s+/).filter(Boolean);
      if (words.length === 0) return;

      const probe = document.createElement('span');
      probe.style.cssText =
        'position:absolute;visibility:hidden;white-space:nowrap;pointer-events:none;';
      wrapper.appendChild(probe);
      const widest = (px: number): number => {
        probe.style.fontSize = `${px}px`;
        let max = 0;
        for (const word of words) {
          probe.textContent = word;
          max = Math.max(max, probe.getBoundingClientRect().width);
        }
        return max;
      };

      let next = maxPx;
      while (next > minPx && widest(next) > available) next -= 1;
      wrapper.removeChild(probe);
      setSize(next);
    };

    fit();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', fit);
      return () => window.removeEventListener('resize', fit);
    }
    const observer = new ResizeObserver(fit);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [wrapperRef, text, maxPx, minPx]);

  return size;
}

function DividerLabelText({
  text,
  visible,
  testId,
  wrapperStyle,
  textStyle,
  maxFontSizePx,
  minFontSizePx,
  onFit,
  appliedFontSizePx,
}: DividerLabelTextProps): React.ReactElement {
  const ref = useRef<HTMLSpanElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const fittedSize = useFittedFontSize(wrapperRef, text, maxFontSizePx, minFontSizePx);
  useLayoutEffect(() => {
    onFit(fittedSize);
  }, [fittedSize, onFit]);
  const fontSize = appliedFontSizePx;
  const lineWidth = useLongestLineWidth(ref, text, fontSize);

  return (
    <div
      ref={wrapperRef}
      data-testid={testId}
      style={{ ...wrapperStyle, opacity: visible ? 1 : 0 }}
    >
      <span
        ref={ref}
        style={{
          ...textStyle,
          fontSize: `${fontSize}px`,
          width: lineWidth !== undefined ? `${lineWidth}px` : undefined,
        }}
      >
        {text}
      </span>
    </div>
  );
}

interface DragGesture {
  pointerId: number;
  startClientX: number;
  startDivLeft: number;
  startSide: Side;
  movedPx: number;
  isDragging: boolean;
}

function DesktopComparisonBody({
  block,
  tokens,
  styles,
  stackImage,
  option1TitleHtml,
  option1BodyHtml,
  option2TitleHtml,
  option2BodyHtml,
  labelA,
  labelB,
  markSeen,
  lessonThemeColor,
  currentLocale,
  transcriptOperations,
  onSideSelected,
  onSideVideoPlaybackStarted,
  onSideVideoPlaybackCompleted,
}: SharedRenderArgs): React.ReactElement {
  const [settled, setSettled] = useState<Side>('a');
  const [labelSide, setLabelSide] = useState<Side>('a');
  const [fitA, setFitA] = useState<number | undefined>(undefined);
  const [fitB, setFitB] = useState<number | undefined>(undefined);
  const [panelHeight, setPanelHeight] = useState<number | null>(null);

  const stageRef = useRef<HTMLDivElement | null>(null);
  const panelARef = useRef<HTMLDivElement | null>(null);
  const panelBRef = useRef<HTMLDivElement | null>(null);
  const clipARef = useRef<HTMLDivElement | null>(null);
  const clipBRef = useRef<HTMLDivElement | null>(null);
  const fadeARef = useRef<HTMLDivElement | null>(null);
  const fadeBRef = useRef<HTMLDivElement | null>(null);
  const dividerRef = useRef<HTMLDivElement | null>(null);
  const videoARef = useRef<MediaPlayerInstance | null>(null);
  const videoBRef = useRef<MediaPlayerInstance | null>(null);

  const containerWidthRef = useRef(0);
  const dividerWidthRef = useRef(0);
  const divLeftRef = useRef(0);
  const dragRef = useRef<DragGesture | null>(null);
  const fadeRafRef = useRef<number | null>(null);
  const settledRef = useRef<Side>('a');
  settledRef.current = settled;
  const labelSideRef = useRef<Side>('a');
  labelSideRef.current = labelSide;

  // ---- Geometry helpers ----
  const posA = useCallback((): number => {
    return Math.max(0, containerWidthRef.current - dividerWidthRef.current);
  }, []);

  const writeDividerStyles = useCallback((divLeft: number): void => {
    const cw = containerWidthRef.current;
    const dw = dividerWidthRef.current;
    const clipBWidth = Math.max(0, cw - divLeft - dw);

    if (dividerRef.current) {
      dividerRef.current.style.left = `${divLeft}px`;
      dividerRef.current.style.width = `${dw}px`;
    }
    if (clipARef.current) {
      clipARef.current.style.width = `${divLeft}px`;
    }
    if (clipBRef.current) {
      clipBRef.current.style.width = `${clipBWidth}px`;
    }
  }, []);

  const writePanelWidths = useCallback((): void => {
    const panelWidth = Math.max(0, containerWidthRef.current - dividerWidthRef.current);
    if (panelARef.current) panelARef.current.style.width = `${panelWidth}px`;
    if (panelBRef.current) panelBRef.current.style.width = `${panelWidth}px`;
  }, []);

  const writeOverlayOpacities = useCallback((divLeft: number): void => {
    const max = posA();
    const progress = max <= 0 ? 0 : divLeft / max;
    if (fadeARef.current) {
      fadeARef.current.style.opacity = `${1 - progress}`;
    }
    if (fadeBRef.current) {
      fadeBRef.current.style.opacity = `${progress}`;
    }
  }, [posA]);

  const setTransitionEnabled = useCallback((enabled: boolean, durationMs?: number): void => {
    const d = durationMs ?? SNAP_DURATION_MS;
    const t = enabled
      ? `left ${d}ms ${SNAP_TIMING}, width ${d}ms ${SNAP_TIMING}`
      : 'none';
    if (dividerRef.current) dividerRef.current.style.transition = t;
    if (clipARef.current) clipARef.current.style.transition = t;
    if (clipBRef.current) clipBRef.current.style.transition = t;
  }, []);

  const cancelFadeRaf = useCallback((): void => {
    if (fadeRafRef.current !== null) {
      cancelAnimationFrame(fadeRafRef.current);
      fadeRafRef.current = null;
    }
  }, []);

  // Drive both fade overlays from their current opacity to zero. Used after a
  // drag-release; the duration scales with the snap distance so the fade
  // tracks the slide.
  const fadeOverlaysOut = useCallback((durationMs: number = OVERLAY_FADE_MS): void => {
    cancelFadeRaf();
    const fadeAEl = fadeARef.current;
    const fadeBEl = fadeBRef.current;
    if (!fadeAEl || !fadeBEl) return;

    const startA = parseFloat(fadeAEl.style.opacity || '0') || 0;
    const startB = parseFloat(fadeBEl.style.opacity || '0') || 0;

    if (durationMs <= 0) {
      fadeAEl.style.opacity = '0';
      fadeBEl.style.opacity = '0';
      return;
    }

    const startTime = performance.now();

    const tick = (now: number): void => {
      const t = Math.min(1, (now - startTime) / durationMs);
      const eased = easeOut(t);
      fadeAEl.style.opacity = `${startA * (1 - eased)}`;
      fadeBEl.style.opacity = `${startB * (1 - eased)}`;
      if (t < 1) {
        fadeRafRef.current = requestAnimationFrame(tick);
      } else {
        fadeRafRef.current = null;
      }
    };
    fadeRafRef.current = requestAnimationFrame(tick);
  }, [cancelFadeRaf]);

  // Drive the incoming-side overlay from full opacity to zero in lockstep
  // with the click-toggle slide. Used after a click-toggle.
  const fadeIncomingOverlayOut = useCallback((incoming: Side, durationMs: number = OVERLAY_FADE_MS): void => {
    cancelFadeRaf();
    const target = incoming === 'a' ? fadeARef.current : fadeBRef.current;
    const other = incoming === 'a' ? fadeBRef.current : fadeARef.current;
    if (!target) return;

    if (other) other.style.opacity = '0';
    target.style.opacity = '1';
    const startTime = performance.now();

    const tick = (now: number): void => {
      const t = Math.min(1, (now - startTime) / durationMs);
      target.style.opacity = `${1 - easeOut(t)}`;
      if (t < 1) {
        fadeRafRef.current = requestAnimationFrame(tick);
      } else {
        fadeRafRef.current = null;
      }
    };
    fadeRafRef.current = requestAnimationFrame(tick);
  }, [cancelFadeRaf]);

  // Update labelSide based on midpoint crossing during drag.
  const maybeFlipLabel = useCallback((divLeft: number): void => {
    const mid = posA() / 2;
    const next: Side = divLeft >= mid ? 'a' : 'b';
    if (next !== labelSideRef.current) {
      labelSideRef.current = next;
      setLabelSide(next);
    }
  }, [posA]);

  const pauseVideo = useCallback((side: Side): void => {
    const ref = side === 'a' ? videoARef : videoBRef;
    ref.current?.pause();
  }, []);

  // ---- Container measurement (mount + resize) ----
  // Note: this runs once on mount and once per resize. It does NOT depend on
  // `settled`, so click-toggles and drag-snaps don't re-trigger measurement
  // and clobber an in-flight transition.
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    // `cursor` lives outside the React style prop so a re-render during drag
    // (e.g. when labelSide flips at midpoint) doesn't clobber `grabbing` back
    // to `grab`.
    if (dividerRef.current && !dividerRef.current.style.cursor) {
      dividerRef.current.style.cursor = 'grab';
    }
    // Fade overlays default to opacity 1 (fully opaque white) without an
    // explicit value. Initialize to 0 imperatively so the rest state shows
    // panel content, and so React re-renders mid-drag don't reset them.
    if (fadeARef.current && fadeARef.current.style.opacity === '') {
      fadeARef.current.style.opacity = '0';
    }
    if (fadeBRef.current && fadeBRef.current.style.opacity === '') {
      fadeBRef.current.style.opacity = '0';
    }

    const measureAndApply = (): void => {
      const cw = stage.getBoundingClientRect().width;
      if (cw <= 0) return;
      const dw = computeDividerWidth(cw);
      containerWidthRef.current = cw;
      dividerWidthRef.current = dw;

      writePanelWidths();

      // Re-anchor divider to the current settled side. If the user is
      // mid-drag, dragRef is set and we leave divLeftRef alone.
      if (dragRef.current === null) {
        const restLeft = settledRef.current === 'a' ? Math.max(0, cw - dw) : 0;
        divLeftRef.current = restLeft;
        // Resize during a transitionless rest state should not animate.
        setTransitionEnabled(false);
        writeDividerStyles(restLeft);
      } else {
        // Update only the widths so the panels and clip widths stay in sync;
        // divider position is being driven by the active drag.
        writeDividerStyles(divLeftRef.current);
      }
    };

    measureAndApply();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measureAndApply);
      return () => window.removeEventListener('resize', measureAndApply);
    }
    const observer = new ResizeObserver(measureAndApply);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [setTransitionEnabled, writeDividerStyles, writePanelWidths]);

  // ---- Equal-height measurement: max(hA, hB) → stage.minHeight ----
  useLayoutEffect(() => {
    const a = panelARef.current;
    const b = panelBRef.current;
    if (!a || !b) return;

    const recompute = (): void => {
      const ha = a.scrollHeight;
      const hb = b.scrollHeight;
      const next = Math.max(ha, hb);
      if (next > 0) setPanelHeight(next);
    };

    recompute();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(recompute);
    observer.observe(a);
    observer.observe(b);
    return () => observer.disconnect();
  }, [
    block.option1,
    block.option2,
    block.contentType,
    block.imageSize,
    option1TitleHtml,
    option1BodyHtml,
    option2TitleHtml,
    option2BodyHtml,
  ]);

  useEffect(() => {
    return () => cancelFadeRaf();
  }, [cancelFadeRaf]);

  // ---- Click-toggle (slides over the full snap duration with the incoming
  // overlay fading in lockstep) ----
  const performClickToggle = useCallback((): void => {
    const current = settledRef.current;
    const next: Side = current === 'a' ? 'b' : 'a';
    pauseVideo(current);
    setTransitionEnabled(true, CLICK_SLIDE_MS);
    // Force a layout read so the browser commits the transition rule before
    // we change `left`/`width` (otherwise some browsers coalesce).
    if (dividerRef.current) {
      void dividerRef.current.offsetWidth;
    }
    const target = next === 'a' ? posA() : 0;
    divLeftRef.current = target;
    writeDividerStyles(target);
    fadeIncomingOverlayOut(next, CLICK_SLIDE_MS);
    settledRef.current = next;
    labelSideRef.current = next;
    setSettled(next);
    setLabelSide(next);
    if (next === 'b') markSeen('b');
    onSideSelected?.(next === 'a' ? 1 : 2);
  }, [
    pauseVideo,
    setTransitionEnabled,
    posA,
    writeDividerStyles,
    fadeIncomingOverlayOut,
    markSeen,
    onSideSelected,
  ]);

  // ---- Pointer events (unified mouse + touch + pen) ----
  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>): void => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      const dividerEl = dividerRef.current;
      if (!dividerEl) return;
      try {
        dividerEl.setPointerCapture(event.pointerId);
      } catch {
        // jsdom and very old browsers may not implement this — ignore.
      }
      cancelFadeRaf();
      setTransitionEnabled(false);
      dragRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startDivLeft: divLeftRef.current,
        startSide: settledRef.current,
        movedPx: 0,
        isDragging: false,
      };
    },
    [cancelFadeRaf, setTransitionEnabled]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>): void => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;

      const dx = event.clientX - drag.startClientX;
      drag.movedPx = Math.max(drag.movedPx, Math.abs(dx));

      if (!drag.isDragging) {
        if (drag.movedPx < DRAG_THRESHOLD_PX) return;
        drag.isDragging = true;
        if (dividerRef.current) {
          dividerRef.current.style.cursor = 'grabbing';
        }
      }

      const max = posA();
      const next = Math.max(0, Math.min(max, drag.startDivLeft + dx));
      divLeftRef.current = next;
      writeDividerStyles(next);
      writeOverlayOpacities(next);
      maybeFlipLabel(next);
    },
    [posA, writeDividerStyles, writeOverlayOpacities, maybeFlipLabel]
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>): void => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const dividerEl = dividerRef.current;
      if (dividerEl) {
        try {
          dividerEl.releasePointerCapture(event.pointerId);
        } catch {
          // ignore
        }
        dividerEl.style.cursor = 'grab';
      }
      dragRef.current = null;

      if (!drag.isDragging) {
        // Click without sufficient travel — teleport.
        performClickToggle();
        return;
      }

      // Drag-release: snap by direction of net travel; zero net travel snaps
      // back to the side the gesture started on.
      const netTravel = divLeftRef.current - drag.startDivLeft;
      let target: Side;
      if (netTravel > 0) target = 'a';
      else if (netTravel < 0) target = 'b';
      else target = drag.startSide;

      const targetLeft = target === 'a' ? posA() : 0;
      // Pause whichever video was "active" before the snap (the side leaving
      // visibility). If we're snapping back to the start side, no side change
      // — nothing to pause.
      if (target !== drag.startSide) {
        pauseVideo(drag.startSide);
      }

      // Scale snap + fade durations to the remaining distance so the apparent
      // speed of the snap is constant regardless of where the user released.
      const fullTravel = posA();
      const distance = Math.abs(targetLeft - divLeftRef.current);
      const fraction = fullTravel > 0 ? distance / fullTravel : 0;
      const snapDuration = SNAP_DURATION_MS * fraction;
      const fadeDuration = OVERLAY_FADE_MS * fraction;

      setTransitionEnabled(true, snapDuration);
      // Force a layout read so the browser commits the transition rule before
      // we change `left`/`width` (otherwise some browsers coalesce).
      if (dividerRef.current) {
        void dividerRef.current.offsetWidth;
      }
      divLeftRef.current = targetLeft;
      writeDividerStyles(targetLeft);
      fadeOverlaysOut(fadeDuration);
      settledRef.current = target;
      labelSideRef.current = target;
      setSettled(target);
      setLabelSide(target);
      if (target === 'b') markSeen('b');
      if (target !== drag.startSide) onSideSelected?.(target === 'a' ? 1 : 2);
    },
    [
      performClickToggle,
      posA,
      pauseVideo,
      setTransitionEnabled,
      writeDividerStyles,
      fadeOverlaysOut,
      markSeen,
      onSideSelected,
    ]
  );

  // ---- Static styles ----
  // Padding mirrors AA-144's outer/inner asymmetry — the seam-side gets more
  // breathing room because the knob sits there.
  const contentPadY = tokens.spacing['2xl'];
  const contentPadOuter = tokens.spacing['3xl'];
  const contentPadInner = tokens.spacing['4xl'];
  const knobReservedPadding = `calc(${KNOB_SIZE_PX / 2}px + ${tokens.spacing.lg})`;

  const stageStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    minHeight: panelHeight ?? undefined,
    boxSizing: 'border-box',
    overflow: 'hidden',
  };

  // Note: `width`, `left`, `right`, `transition`, `opacity`, and `cursor` are
  // intentionally NOT declared in any of the React style props below for the
  // managed elements (panels, clips, fades, divider). Those properties are
  // written imperatively via refs, and including them in the style prop would
  // cause React to overwrite them on every re-render.
  const panelABaseStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    minWidth: 0,
    boxSizing: 'border-box',
    paddingTop: contentPadY,
    paddingBottom: contentPadY,
    paddingLeft: contentPadOuter,
    paddingRight: contentPadInner,
  };

  const panelBBaseStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    right: 0,
    minWidth: 0,
    boxSizing: 'border-box',
    paddingTop: contentPadY,
    paddingBottom: contentPadY,
    paddingLeft: contentPadInner,
    paddingRight: contentPadOuter,
  };

  const clipABaseStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box',
  };

  const clipBBaseStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box',
  };

  const fadeBaseStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    backgroundColor: tokens.backgrounds.app,
    pointerEvents: 'none',
  };

  const dividerBaseStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    height: '100%',
    // No border-radius. The shell's `overflow: hidden` already clips the
    // outer corners against the shell radius; rounded inner corners would
    // visibly translate with the divider during a drag.
    borderRadius: 0,
    backgroundColor: tokens.buttons.action.bg,
    boxSizing: 'border-box',
    touchAction: 'none',
    userSelect: 'none',
    color: tokens.text.onInverse,
    fontFamily: tokens.typography.fontFamily,
    zIndex: 2,
  };

  const dividerLabelContainerStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    boxSizing: 'border-box',
    fontSize: tokens.typography.sizes.lg,
    fontWeight: tokens.typography.weights.regular,
    // Tighter than CSS `normal` so multi-line label rendering matches the
    // Figma file's "Auto" line-height for Source Sans Pro.
    lineHeight: 1.27,
    color: tokens.text.onInverse,
    pointerEvents: 'none',
  };

  // Each label sits in its own flex wrapper. The wrapper carries the
  // opacity-fade for label switching; the inner span is measured to its
  // longest line and centered via auto margins (mirrors the AA-144 logic
  // for the mobile sidebar).
  const dividerLabelWrapperBaseStyle: React.CSSProperties = {
    position: 'absolute',
    left: knobReservedPadding,
    right: knobReservedPadding,
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    pointerEvents: 'none',
    boxSizing: 'border-box',
  };

  const dividerLabelTextBaseStyle: React.CSSProperties = {
    display: 'block',
    maxWidth: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 0,
    marginBottom: 0,
    textAlign: 'left',
    // Last resort when a word still overflows at the minimum font size.
    overflowWrap: 'anywhere',
  };
  const dividerLabelMaxFontPx = parseFloat(tokens.typography.sizes.lg);
  const dividerLabelMinFontPx = parseFloat(tokens.typography.sizes.base);
  const dividerLabelFontPx = Math.min(fitA ?? dividerLabelMaxFontPx, fitB ?? dividerLabelMaxFontPx);

  // The knob sits on the side of the divider closer to the currently labelled
  // side: labelSide 'a' → knob on left edge (chevron pointing left toward B);
  // labelSide 'b' → knob on right edge (chevron pointing right toward A).
  // Edge position uses 0 / 100% so it tracks the divider's actual width.
  const knobLeft = labelSide === 'a' ? '0%' : '100%';
  const knobTransform = labelSide === 'a'
    ? 'translate(-50%, -50%)'
    : 'translate(-50%, -50%) scaleX(-1)';

  return (
    <div ref={stageRef} data-testid="comparison-stage" style={stageStyle}>
      <div ref={clipARef} data-testid="comparison-clip-a" style={clipABaseStyle}>
        <div
          ref={panelARef}
          data-testid="comparison-panel-a"
          style={panelABaseStyle}
        >
          <PanelContent
            option={block.option1}
            titleHtml={option1TitleHtml}
            bodyHtml={option1BodyHtml}
            contentType={block.contentType}
            imageSize={block.imageSize}
            stackImage={stackImage}
            tokens={tokens}
            styles={styles}
            videoRef={videoARef}
            lessonThemeColor={lessonThemeColor}
            currentLocale={currentLocale}
            transcriptOperations={transcriptOperations}
            onVideoPlaybackStarted={() => onSideVideoPlaybackStarted?.(1)}
            onVideoPlaybackCompleted={() => onSideVideoPlaybackCompleted?.(1)}
          />
        </div>
        <div ref={fadeARef} data-testid="comparison-fade-a" style={fadeBaseStyle} />
      </div>

      <div ref={clipBRef} data-testid="comparison-clip-b" style={clipBBaseStyle}>
        <div
          ref={panelBRef}
          data-testid="comparison-panel-b"
          style={panelBBaseStyle}
        >
          <PanelContent
            option={block.option2}
            titleHtml={option2TitleHtml}
            bodyHtml={option2BodyHtml}
            contentType={block.contentType}
            imageSize={block.imageSize}
            stackImage={stackImage}
            tokens={tokens}
            styles={styles}
            videoRef={videoBRef}
            lessonThemeColor={lessonThemeColor}
            currentLocale={currentLocale}
            transcriptOperations={transcriptOperations}
            onVideoPlaybackStarted={() => onSideVideoPlaybackStarted?.(2)}
            onVideoPlaybackCompleted={() => onSideVideoPlaybackCompleted?.(2)}
          />
        </div>
        <div ref={fadeBRef} data-testid="comparison-fade-b" style={fadeBaseStyle} />
      </div>

      <div
        ref={dividerRef}
        role="button"
        tabIndex={0}
        data-testid="comparison-divider"
        aria-label={labelSide === 'a' ? labelA : labelB}
        style={dividerBaseStyle}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            performClickToggle();
          }
        }}
      >
        <div data-testid="comparison-divider-label" style={dividerLabelContainerStyle}>
          <DividerLabelText
            text={labelA}
            visible={labelSide === 'a'}
            testId="comparison-divider-label-a"
            wrapperStyle={dividerLabelWrapperBaseStyle}
            textStyle={dividerLabelTextBaseStyle}
            maxFontSizePx={dividerLabelMaxFontPx}
            minFontSizePx={dividerLabelMinFontPx}
            appliedFontSizePx={dividerLabelFontPx}
            onFit={setFitA}
          />
          <DividerLabelText
            text={labelB}
            visible={labelSide === 'b'}
            testId="comparison-divider-label-b"
            wrapperStyle={dividerLabelWrapperBaseStyle}
            textStyle={dividerLabelTextBaseStyle}
            maxFontSizePx={dividerLabelMaxFontPx}
            minFontSizePx={dividerLabelMinFontPx}
            appliedFontSizePx={dividerLabelFontPx}
            onFit={setFitB}
          />
        </div>
        <span
          data-testid="comparison-knob"
          style={{
            position: 'absolute',
            top: '50%',
            left: knobLeft,
            width: `${KNOB_SIZE_PX}px`,
            height: `${KNOB_SIZE_PX}px`,
            transform: knobTransform,
            zIndex: 1,
            display: 'block',
            boxSizing: 'border-box',
          }}
        >
          <ComparisonSliderKnobIcon
            fill={lessonThemeColor ?? tokens.ui.fixedAccent}
            stroke={getReadableTextColor(lessonThemeColor ?? tokens.ui.fixedAccent)}
          />
        </span>
      </div>
    </div>
  );
}
